import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, finalize, firstValueFrom, map, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { KeycloakService } from './keycloak.service';
import {
  ACTING_POST_CONFIRMED_KEY,
  ActingPost,
  GetActingPostResponse,
  SetActingPostRequest,
  actingPostFromApi,
} from './acting-post.model';

/**
 * Single owner of "which posting is the user acting as" — see the acting-post
 * spec §8.2. `activeNodeCd`/`activePosting`/`activeLabel` are ALWAYS derived
 * from KeycloakService.userContext$ (via toSignal), never written directly:
 * the only thing that ever moves the active node is a fresh `/auth/validate`
 * response (a reload, or KeycloakService.refreshUserContext() on the login
 * path). This is what makes a failed switch trivially correct — the UI never
 * held the value, so there is nothing to roll back.
 */
@Injectable({ providedIn: 'root' })
export class ActingPostService {
  private readonly http = inject(HttpClient);
  private readonly keycloak = inject(KeycloakService);

  private readonly ctx = toSignal(this.keycloak.userContext$, { initialValue: null });

  readonly postings = signal<ActingPost[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly switching = signal(false);
  readonly error = signal<string | null>(null);

  /** Permanently downgrades to feature-off after a 404/501 from the postings
   * endpoint (spec §8.1 point 4) — a transient 500/timeout does NOT set this,
   * so a flaky network doesn't silently drop a mandatory step. */
  private readonly forcedOff = signal(false);

  private readonly isConfirmedSession = signal<boolean>(this.readConfirmedState());

  readonly featureEnabled = computed(() => {
    if (this.forcedOff()) return false;
    const c = this.ctx() as any;
    return !!c && c.actingPostEnabled === true;
  });

  readonly contextPending = computed(() => {
    if (this.forcedOff()) return false;
    const c = this.ctx() as any;
    return !c || c.isAuthoritative !== true;
  });

  constructor() {
    effect(() => {
      // Don't load postings until OTP is verified (when 2FA is on).
      // The user context carries otpRequired/otpVerified from /auth/validate.
      const c = this.ctx() as any;
      const otpPending = !!environment.enableOtp && c?.otpRequired && !c?.otpVerified;
      if (this.featureEnabled() && !this.loaded() && !this.loading() && !this.error() && !otpPending) {
        this.loadPostings();
      }
    });
  }

  /** True once THIS login's posting has been confirmed. Backed by
   * sessionStorage so it survives the switcher's own mandatory reload,
   * but never survives a new login (see KeycloakService's confirmed-key
   * clearing in clearTokens/logout/loginWithCredentials). */
  readonly confirmed = computed(() => {
    const isConfirmedMem = this.isConfirmedSession();
    const c = this.ctx() as any;
    const currentUserId = c?.userId;
    const state = this.readConfirmedState();
    try {
      const val = sessionStorage.getItem(ACTING_POST_CONFIRMED_KEY);
      if (currentUserId && val && val !== 'true' && val !== currentUserId) {
        return false;
      }
    } catch {
      // ignore
    }
    return state || isConfirmedMem;
  });

  readonly activeNodeCd = computed(() => {
    const n = (this.ctx() as any)?.nodeCd;
    return n !== undefined && n !== null && n !== '' ? String(n) : null;
  });
  readonly activePosting = computed(
    () => this.postings().find((p) => p.nodeCd === this.activeNodeCd()) ?? null
  );
  readonly activeLabel = computed(
    () => this.activePosting()?.displayName ?? (this.ctx() as any)?.nodeName ?? ''
  );
  readonly remembered = computed(() => this.postings().find((p) => p.isRemembered) ?? null);

  /** GET the posting options. No-op (issues no request) when the feature is
   * disabled — required so a flag-off build never makes this call. */
  loadPostings(): void {
    if (!this.featureEnabled() || this.loading()) {
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.http
      .get<GetActingPostResponse>(`${environment.apiBaseUrl}/auth/acting-post`)
      .pipe(
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (res: GetActingPostResponse) => {
          this.postings.set((res.options || []).map(actingPostFromApi));
          this.loaded.set(true);
        },
        error: (err: HttpErrorResponse) => {
          this.loaded.set(true);
          if (err.status === 404 || err.status === 501) {
            // The endpoint doesn't exist in this deployment — permanently
            // downgrade rather than retry-loop against a route that will never
            // resolve.
            this.forcedOff.set(true);
          } else {
            this.error.set('Could not load your postings.');
          }
        },
      });
  }

  /** POST the selection. Never mutates activeNodeCd on success — the caller
   * (a reload, or refreshUserContext() on the login path) is what moves it. */
  setActingPost(nodeCd: string): Observable<void> {
    this.switching.set(true);
    this.error.set(null);
    const body: SetActingPostRequest = { node_cd: nodeCd };
    return this.http.post<GetActingPostResponse>(`${environment.apiBaseUrl}/auth/acting-post`, body).pipe(
      map(() => void 0),
      catchError((err: HttpErrorResponse) => {
        return throwError(() => err);
      }),
      finalize(() => {
        this.switching.set(false);
      })
    );
  }

  markConfirmed(): void {
    try {
      const userId = (this.ctx() as any)?.userId || 'true';
      sessionStorage.setItem(ACTING_POST_CONFIRMED_KEY, userId);
    } catch {
      // sessionStorage unavailable (private browsing etc.) — fall through to
      // the in-memory signal so this session's confirmation still holds.
    }
    this.isConfirmedSession.set(true);
    this.switching.set(false);
  }

  clearConfirmed(): void {
    try {
      sessionStorage.removeItem(ACTING_POST_CONFIRMED_KEY);
    } catch {
      // ignore
    }
    this.isConfirmedSession.set(false);
  }

  /** Where the login flow should go next: the picker when the feature is on,
   * straight to /modules when it's off — the single seam both login paths
   * (password grant and OTP) call, so a flag-off build never routes to a
   * picker (spec §8.1 point 3). */
  postLoginRoute(): string {
    return this.featureEnabled() ? '/login/posting' : '/modules';
  }

  /**
   * Async handler after login authentication: fetches assigned postings.
   * If user has exactly 1 posting, auto-confirms it and returns the final target route
   * (e.g. /modules or returnUrl), bypassing the /login/posting screen completely.
   * If user has multiple postings, returns '/login/posting' so the user can select.
   * If the user has NO posting assigned (an empty options list), also returns
   * '/login/posting' rather than the dashboard — that screen already has a dedicated
   * "no posting assigned" empty state (with a "login as another user" action) for exactly
   * this case, so login must not silently proceed past it into a guarded route the user
   * has no confirmed posting for.
   */
  async handlePostLogin(returnUrl?: string): Promise<string> {
    const target = returnUrl || '/modules';
    if (!this.featureEnabled()) {
      return target;
    }

    try {
      this.loading.set(true);
      const res = await firstValueFrom(
        this.http.get<GetActingPostResponse>(`${environment.apiBaseUrl}/auth/acting-post`)
      );
      this.loading.set(false);
      const options = (res?.options || []).map(actingPostFromApi);
      this.postings.set(options);
      this.loaded.set(true);

      if (options.length === 1) {
        // Auto-select single posting immediately without navigating to /login/posting screen
        await firstValueFrom(this.setActingPost(options[0].nodeCd));
        this.markConfirmed();
        this.keycloak.refreshUserContext().catch((e) => {
          console.warn('[ActingPostService] refreshUserContext failed (non-blocking):', e);
        });
        return target;
      }
      // options.length === 0 (no posting assigned) or > 1 (needs a manual pick) both land
      // on /login/posting — its empty-state branch handles the zero-option case.
      return '/login/posting';
    } catch (err) {
      this.loading.set(false);
      return '/login/posting';
    }
  }

  private readConfirmedState(): boolean {
    try {
      const val = sessionStorage.getItem(ACTING_POST_CONFIRMED_KEY);
      if (!val || val === 'false') {
        return false;
      }
      const currentUserId = (this.ctx() as any)?.userId;
      if (currentUserId && val !== 'true') {
        return val === currentUserId;
      }
      return true;
    } catch {
      return false;
    }
  }
}
